import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import buildImageUrl from '../utils/imageUrl';
import './PastThrows.css';

const PastThrows = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const dragIdx = useRef(null);
  const overIdx = useRef(null);

  const fetchGallery = useCallback(async () => {
    try {
      const endpoint = isAdmin ? '/admin/gallery' : '/gallery';
      const response = await api.get(endpoint);
      setImages(response.data);
    } catch (err) {
      console.error('Failed to load gallery', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        await api.post('/admin/gallery', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await fetchGallery();
    } catch (err) {
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      fetchGallery();
    } catch (err) {
      alert('Failed to delete image');
    }
  };

  const onDragStart = (e, index) => {
    dragIdx.current = index;
    e.dataTransfer.effectAllowed = 'move';
    const el = e.currentTarget;
    requestAnimationFrame(() => el.classList.add('dragging'));
  };

  const onDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    document.querySelectorAll('.drag-over').forEach((el) => el.classList.remove('drag-over'));
  };

  const onDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    overIdx.current = index;
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const onDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const onDrop = async (e) => {
    e.preventDefault();
    document.querySelectorAll('.drag-over').forEach((el) => el.classList.remove('drag-over'));

    const from = dragIdx.current;
    const to = overIdx.current;
    if (from === null || to === null || from === to) return;

    const reordered = [...images];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setImages(reordered);

    const updates = reordered.map((img, i) => ({ id: img.id, sort_order: i }));
    try {
      await api.put('/admin/gallery/reorder', { images: updates });
    } catch (err) {
      fetchGallery();
    }

    dragIdx.current = null;
    overIdx.current = null;
  };

  if (loading) {
    return <div className="loading">Loading gallery...</div>;
  }

  return (
    <div className="past-throws">
      <div className="past-throws-header">
        <h1 className="past-throws-title">Past Throws</h1>
        <p className="past-throws-subtitle">A look back at pieces that found their homes</p>
        {isAdmin && (
          <p className="admin-hint">Drag images to rearrange</p>
        )}
      </div>

      {images.length === 0 && !isAdmin ? (
        <div className="empty-state">
          <h2>No gallery images yet</h2>
          <p>Check back soon!</p>
        </div>
      ) : (
        <>
          <div className="gallery-masonry">
            {images.map((img, index) => (
              <div
                key={img.id}
                className={`gallery-item ${isAdmin ? 'gallery-item-admin' : ''}`}
                draggable={isAdmin}
                onDragStart={isAdmin ? (e) => onDragStart(e, index) : undefined}
                onDragEnd={isAdmin ? onDragEnd : undefined}
                onDragOver={isAdmin ? (e) => onDragOver(e, index) : undefined}
                onDragEnter={isAdmin ? onDragEnter : undefined}
                onDragLeave={isAdmin ? onDragLeave : undefined}
                onDrop={isAdmin ? onDrop : undefined}
              >
                <img
                  src={buildImageUrl(img.image_url)}
                  alt=""
                  className="gallery-image"
                  loading="lazy"
                />
                {isAdmin && (
                  <button
                    className="gallery-delete-btn"
                    onClick={() => handleDelete(img.id)}
                    title="Delete image"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          {isAdmin && (
            <div className="gallery-upload-area">
              {uploading && (
                <div className="upload-overlay">
                  <div className="upload-spinner" />
                  <span>Uploading...</span>
                </div>
              )}
              <label className={`gallery-upload-btn ${uploading ? 'uploading' : ''}`}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                <span>{uploading ? 'Uploading...' : '+ Add Images'}</span>
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PastThrows;
